import React from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import {
  addDays,
  format,
  isAfter,
  isBefore,
  isValid,
  differenceInDays,
  formatSlash,
} from '@qover/utils/src/date';
import { Trans } from '@lingui/macro';
import has from 'lodash/has';
import { toDate } from '@qover/utils/src/date';

const isEmpty = (value: any) =>
  value === undefined || value === null || value === '';
const join = (rules: any) => (value: any, data: any) =>
  rules
    .map((rule: any) => rule(value, data))
    .filter((error: any) => !!error)[0]; /* first error */

const emailRegex = /^(?:[A-Za-z0-9_!#$%&'*+-/=?^`{|}~]+\.)*[A-Za-z0-9_!#$%&'*+-/=?^`{|}~]+@(?:(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-](?!\.)){0,61}[a-zA-Z0-9]?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-](?!$)){0,61}[a-zA-Z0-9]?)|(?:\[(?:(?:[01]?\d{1,2}|2[0-4]\d|25[0-5])\.){3}(?:[01]?\d{1,2}|2[0-4]\d|25[0-5])\]))$/i; // eslint-disable-line max-len

const passwordRegexp = new RegExp(
  '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&+-])[A-Za-z0-9$@$!%*?&+-]{8,}',
);

export const messages = {
  doNotMatch: <Trans id="error.doNotMatch" />,
  doNotMatchPattern: (regex: string) => (
    <Trans id="error.doNotMatchPattern" defaults="{regex}" values={{ regex }} />
  ),
  emailInvalid: <Trans id="general.emailInvalid" />,
  invalidDate: <Trans id="error.invalidDate" />,
  minDateAdult: (minDate: string) => (
    <Trans id="error.minDateAdult" defaults="{minDate}" values={{ minDate }} />
  ),
  maxDateAdult: (maxDate: string) => (
    <Trans id="error.maxDateAdult" defaults="{maxDate}" values={{ maxDate }} />
  ),
  maxDate: (maxDate: string) => (
    <Trans id="error.maxDate" defaults="{maxDate}" values={{ maxDate }} />
  ),
  maxLength: (maximum: string) => (
    <Trans id="error.maxLength" defaults="{maximum}" values={{ maximum }} />
  ),
  minDate: (minDate: string) => (
    <Trans id="error.minDate" defaults="{minDate}" values={{ minDate }} />
  ),
  minLength: (minimum: string) => (
    <Trans id="error.minLength" defaults="{minimum}" values={{ minimum }} />
  ),
  exactLength: (length: string) => (
    <Trans id="error.exactLength" defaults="{length}" values={{ length }} />
  ),
  minValue: (minValue: number) => (
    <Trans id="error.minValue" defaults="{minValue}" values={{ minValue }} />
  ),
  maxValue: (maxValue: number) => (
    <Trans id="error.maxValue" defaults="{maxValue}" values={{ maxValue }} />
  ),
  notInteger: <Trans id="error.notInteger" />,
  notNumber: <Trans id="error.notNumber" />,
  notOneOf: <Trans id="error.notOneOf" />,
  notString: <Trans id="error.notString" />,
  notTrue: <Trans id="error.notTrue" />,
  oneOf: (enumeration: string) => (
    <Trans id="error.oneOf" defaults="{enumeration}" values={{ enumeration }} />
  ),
  required: <Trans id="error.required" />,
  weakPassword: <Trans id="error.weakPassword" />,
  wrongNumber: <Trans id="error.wrongNumber" />,
  wrongPhoneNumber: <Trans id="error.wrongPhoneNumber" />,
};

export function email(value: any) {
  if (!isEmpty(value) && !emailRegex.test(value)) {
    return messages.emailInvalid;
  }
  return false;
}

export function isTrueWithMessage(message: any) {
  return (value: any) => {
    if (value !== true) {
      return message || messages.notTrue;
    }
    return false;
  };
}

export function isTrue(value: any) {
  if (value !== true) {
    return messages.notTrue;
  }
  return false;
}

// This is use for revert checkbox
export function isFalse(value: any) {
  if (value !== false) {
    return messages.notTrue;
  }
  return false;
}

export function isPhoneNumber(value: any) {
  if (!/^\+[0-9]{7,15}$/.test(value)) {
    return messages.wrongPhoneNumber;
  }
  return false;
}

export function required(value: any) {
  if (isEmpty(value)) {
    return messages.required;
  }
  return false;
}

export function min(minValue: any, message?: JSX.Element) {
  return (value: any) => {
    if (value < minValue) {
      return message || messages.minValue(minValue);
    }
    return false;
  };
}

export function minCurrency(minValue: number, message?: JSX.Element) {
  return (value: number) => {
    if (value < minValue) {
      return message || messages.minValue(minValue / 100);
    }
    return false;
  };
}

export function max(maxValue: any, message?: JSX.Element) {
  return (value: any) => {
    if (value > maxValue) {
      return message || messages.required;
    }
    return false;
  };
}

export function maxCurrency(maxValue: number, message?: JSX.Element) {
  return (value: number) => {
    if (value > maxValue) {
      return message || messages.maxValue(maxValue / 100);
    }
    return false;
  };
}

export function arrayChildValidator(validator: any) {
  return (value: any) => {
    if (value) {
      return validator(value);
    }
    return false;
  };
}

export function arrayMaxLength(maximum: number, message: JSX.Element) {
  return (value: any) => {
    if (!value || value.length > maximum) {
      return message;
    }
    return false;
  };
}
export function arrayMinLength(minimum: number, message: JSX.Element) {
  return (value: any) => {
    if (!value || value.length < minimum) {
      return message;
    }
    return false;
  };
}

export function minLength(minimum: any, message?: JSX.Element) {
  return (value: any) => {
    if (!isEmpty(value) && value.length < minimum) {
      return message || messages.minLength(minimum);
    }
    return false;
  };
}

export function minLengthWithTrim(minimum: any, message?: JSX.Element) {
  return (value: any) => {
    if (!isEmpty(value) && value.trim().length < minimum) {
      return message || messages.minLength(minimum);
    }
    return false;
  };
}

export function exactLength(length: number, message?: JSX.Element) {
  return (value: any) => {
    if (!isEmpty(value) && value.trim().length !== length) {
      return message || messages.exactLength(`${length}`);
    }
    return false;
  };
}

export function isValidDate(message?: JSX.Element) {
  return (value: any) => {
    const date = new Date(value);
    if (!isValid(date)) {
      return message || messages.invalidDate;
    }
    return false;
  };
}

export function minDate(date: Date, message?: any) {
  return (value: any) => {
    const formDate = toDate(value);
    if (!isValid(formDate) || isBefore(formDate, date)) {
      const formattedMinDate = format(date, formatSlash);
      return message
        ? message(formattedMinDate)
        : messages.minDate(formattedMinDate);
    }
    return false;
  };
}

export function minDateDependsOn(field: string) {
  return (value: any, data: any) => {
    const fieldDate = toDate(get(data, field));
    const formDate = toDate(value);

    if (
      !isValid(formDate) ||
      !isValid(fieldDate) ||
      isBefore(formDate, fieldDate)
    ) {
      const minDate = isValid(fieldDate) ? fieldDate : new Date();
      const formattedMinDate = format(minDate, formatSlash);
      return messages.minDate(formattedMinDate);
    }
    return false;
  };
}

export function maxDateDependsOn(field: string, daysToAdd: number) {
  return (value: any, data: any) => {
    const fieldDate = toDate(get(data, field));
    const formDate = toDate(value);

    if (
      !isValid(formDate) ||
      !isValid(fieldDate) ||
      isAfter(formDate, addDays(fieldDate, daysToAdd))
    ) {
      const maxDate = isValid(fieldDate) ? fieldDate : new Date();
      const formattedMaxDate = format(addDays(maxDate, daysToAdd), formatSlash);
      return messages.maxDate(formattedMaxDate);
    }
    return false;
  };
}

export function minDateAdult(date: Date) {
  return (value: any) => {
    const formDate = toDate(value);
    if (!isValid(formDate) || isBefore(formDate, date)) {
      const formattedMinDate = format(date, formatSlash);
      return messages.minDateAdult(formattedMinDate);
    }
    return false;
  };
}

export function maxDate(date: Date) {
  return (value: any) => {
    const formDate = toDate(value);
    if (!isValid(formDate) || isAfter(formDate, date)) {
      const formattedMaxDate = format(date, formatSlash);
      return messages.maxDate(formattedMaxDate);
    }
    return false;
  };
}

export function maxDateAdult(date: Date) {
  return (value: any) => {
    const formDate = toDate(value);
    if (!isValid(formDate) || isAfter(formDate, date)) {
      const formattedMaxDate = format(date, formatSlash);
      return messages.maxDateAdult(formattedMaxDate);
    }
    return false;
  };
}

export function ifSet(rule: any) {
  return (value: any) => {
    if (!isEmpty(value)) {
      return rule(value);
    }
    return false;
  };
}

export function isSet(field: any, fieldValue: any, rule: any) {
  return (value: any, data: any) => {
    if (has(data, field) === fieldValue) {
      return rule(value, data);
    }
    return false;
  };
}

export function dependsOn(field: any, fieldValue: any, rule: any) {
  return (value: any, data: any) => {
    if (get(data, field, false) === fieldValue) {
      return rule(value, data);
    }
    return false;
  };
}

export function dependsOnDiff(field: any, fieldValue: any, rule: any) {
  return (value: any, data: any) => {
    if (get(data, field, false) !== fieldValue) {
      return rule(value, data);
    }
    return false;
  };
}

export function maxLength(maximum: any, message: any) {
  return (value: any) => {
    if (!isEmpty(value) && value.length > maximum) {
      return message || messages.maxLength(maximum);
    }
    return false;
  };
}

export function integer(value: any) {
  if (!Number.isInteger(Number(value))) {
    return messages.notInteger;
  }
  return false;
}

export function number(value: any) {
  if (value && typeof value !== 'number') {
    return messages.notNumber;
  }
  return false;
}

export function isString(value: any) {
  if (!/^[a-zA-Z ]+$/.test(value)) {
    return messages.notString;
  }
  return false;
}

export function oneOf(enumeration: any, message: JSX.Element) {
  return (value: any) => {
    if (enumeration.indexOf(value) === -1) {
      return message || messages.oneOf(enumeration.join(', '));
    }
    return false;
  };
}

export function notOneOf(enumeration: any, message: any) {
  return (value: any) => {
    if (enumeration.indexOf(value) !== -1) {
      return message || messages.notOneOf;
    }
    return false;
  };
}

export function pattern(regex: any, message?: JSX.Element) {
  return (value: any) => {
    if (!regex.test(value)) {
      return message || messages.doNotMatchPattern(regex);
    }
    return false;
  };
}

export function match(field: any, message?: JSX.Element) {
  return (value: any, data: any) => {
    if (data) {
      if (value !== data[field]) {
        return message || messages.doNotMatch;
      }
    }
    return false;
  };
}

export function isPercentage(value: any) {
  if (value && /^\d+(\.\d+)?$/.test(value)) {
    if (Number(value) < 0 || Number(value) > 1) {
      return messages.wrongNumber;
    }
  }
  return false;
}

export function createValidator(rules: any) {
  return (data = {}) => {
    const errors = {};
    Object.keys(rules).forEach(key => {
      // concat enables both functions and arrays of functions
      const rule = join([].concat(rules[key]));
      const error = rule(get(data, key), data);
      if (error) {
        set(errors, key, error);
      }
    });

    return errors;
  };
}

export const composeValidators = (
  validators: { (value: any): false | JSX.Element }[],
) => (value: string) =>
  validators.reduce(
    //@ts-ignore
    (error, validator) => error || validator(value),
    undefined,
  );

export function isEqualOtherField(otherField: any, message: JSX.Element) {
  return (value: any, data: any) => {
    if (value !== get(data, otherField)) {
      return message;
    }
    return false;
  };
}

export function otherFieldRequired(field: any, rule: any) {
  return (value: any, data: any) => {
    if (get(data, field)) {
      return rule(value);
    }

    return false;
  };
}

export function isStrongPassword(value: any) {
  if (!value || !value.match(passwordRegexp)) {
    return messages.weakPassword;
  }

  return false;
}

export function dateDifferenceInDaysTrigger(
  field: string,
  numberOfDays: number,
  rule: (value: any, allValues: any) => any,
) {
  return (value: any, allValues: any) => {
    const dateLeft = toDate(get(allValues, field));
    const dateRight = toDate(value);
    if (differenceInDays(dateLeft, dateRight) > numberOfDays) {
      return rule(value, allValues);
    }

    return false;
  };
}

export function maxAbsoluteDateDifferenceInDays(
  field: string,
  numberOfDays: number,
  message: JSX.Element,
) {
  return (value: any, allValues: any) => {
    const dateLeft = toDate(get(allValues, field));
    const dateRight = toDate(value);

    if (Math.abs(differenceInDays(dateLeft, dateRight)) > numberOfDays) {
      return message;
    }

    return false;
  };
}

export function differentThan(data: any, message: any) {
  return (value: any) => {
    if (data.find((field: any) => field === value)) {
      return (
        message || (
          <Trans
            id="general.error.duplicated.field"
            defaults="Duplicated field"
          />
        )
      );
    }
    return false;
  };
}
